import React from 'react'
import { MDXProvider } from '@mdx-js/react'
import { MDXRenderer } from 'gatsby-plugin-mdx'
import { CodeBlock, hybrid } from 'react-code-blocks'
import { Banner } from './Banner'

const P: React.FC = (props) => (
  <p
    className="last:mb-0 first:mt-0 my-oneline text-base sm:text-lg lg:text-xl first:font-medium font-normal"
    {...props}
  />
)
const H1: React.FC = ({ children, ...props }) => (
  <h1
    className="last:mb-0 first:mt-0 mt-8 text-2xl font-extrabold tracking-tight leading-tight sm:text-3xl lg:text-4xl"
    {...props}
  >
    {children}
  </h1>
)
const H2: React.FC = ({ children, ...props }) => (
  <h2
    className="last:mb-0 first:mt-0 mt-8 text-xl font-extrabold tracking-tight leading-tight sm:text-2xl lg:text-3xl"
    {...props}
  >
    {children}
  </h2>
)
const H3: React.FC = ({ children, ...props }) => (
  <h3
    className="last:mb-0 first:mt-0 mt-8 text-lg font-extrabold tracking-tight leading-tight sm:text-xl lg:text-2xl"
    {...props}
  >
    {children}
  </h3>
)
const H4: React.FC = ({ children, ...props }) => (
  <h4
    className="last:mb-0 first:mt-0 mt-8 text-base font-extrabold tracking-tight leading-tight sm:text-lg lg:text-xl"
    {...props}
  >
    {children}
  </h4>
)
const H5: React.FC = ({ children, ...props }) => (
  <h5
    className="first:mt-0 mt-8 text-base font-extrabold tracking-tight leading-tight sm:text-lg lg:text-xl"
    {...props}
  >
    {children}
  </h5>
)
const H6: React.FC = ({ children, ...props }) => (
  <h6
    className="first:mt-0 mt-8 text-base font-extrabold tracking-tight leading-tight sm:text-lg lg:text-xl"
    {...props}
  >
    {children}
  </h6>
)
const Ul: React.FC = (props) => (
  <ul
    className="last:mb-0 first:mt-0 my-oneline pl-10 list-disc space-y-2 text-base sm:text-lg lg:text-xl"
    {...props}
  />
)
const Ol: React.FC = (props) => (
  <ol
    className="last:mb-0 first:mt-0 my-oneline pl-10 list-decimal space-y-2 text-base sm:text-lg lg:text-xl"
    {...props}
  />
)
const A: React.FC = (props) => (
  <a className="hover:text-blue-500 text-blue-600" {...props} />
)
const B: React.FC = ({ children, ...props }) => (
  <b className="font-bold" {...props}>
    {children}
  </b>
)

const Code: React.FC<{ className?: string }> = ({ children, className }) => {
  const language =
    className?.split('-')[0] === 'language'
      ? className.split('-')[1]
      : 'typescript'

  return (
    <CodeBlock
      text={children}
      language={language}
      theme={hybrid}
      wrapLines
      showLineNumbers={false}
    />
  )
}

const BlockQuote: React.FC = ({ children, ...props }) => (
  <Banner
    {...props}
    innerClassName="py-6 px-8"
    outerClassName="shadow-solid -mx-4 my-12"
  >
    {children}
  </Banner>
)

const Cite: React.FC = ({ children, ...props }) => (
  <cite {...props} className="text-text-secondary-light text-base not-italic">
    {children}
  </cite>
)

const Image: React.FC = (props) => (
  <img {...props} className="border border-border-weak-light" />
)

const Table: React.FC = ({ children, ...props }) => (
  <table
    {...props}
    className="my-oneline text-xs sm:text-sm lg:text-base font-normal min-w-full divide-y divide-border-weak-light border border-border-weak-light"
  >
    {children}
  </table>
)

const THead: React.FC = ({ children, ...props }) => (
  <thead {...props} className="bg-background-three-light">
    {children}
  </thead>
)

const TBody: React.FC = ({ children, ...props }) => (
  <tbody {...props} className="divide-y divide-border-weak-light">
    {children}
  </tbody>
)

const TR: React.FC = ({ children, ...props }) => (
  <tr {...props} className="divide-x divide-border-weak-light">
    {children}
  </tr>
)

const TH: React.FC = ({ children, ...props }) => (
  <th {...props} className="text-left font-semibold px-3 py-1">
    {children}
  </th>
)

const TD: React.FC = ({ children, ...props }) => (
  <td {...props} className="px-3 py-1">
    {children}
  </td>
)

const components = {
  p: P,
  h1: H1,
  h2: H2,
  h3: H3,
  h4: H4,
  h5: H5,
  h6: H6,
  ul: Ul,
  ol: Ol,
  a: A,
  code: Code,
  blockquote: BlockQuote,
  cite: Cite,
  b: B,
  img: Image,
  tr: TR,
  th: TH,
  td: TD,
  thead: THead,
  tbody: TBody,
  table: Table,
}

interface MDXContentProps {
  children: string
}

export const MDXContent: React.FC<MDXContentProps> = ({ children }) => (
  <MDXProvider components={components}>
    <MDXRenderer>{children}</MDXRenderer>
  </MDXProvider>
)
